import { useMemo, FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useDebounce } from 'hooks/useDebounce';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { Button } from 'components/Core/Button';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { StatusCell } from 'components/Core/StatusCell';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { AppLoader } from 'components/Core/AppLoader/AppLoader';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { IChangeCountryStatusReq, ICountryData } from 'stores/countries/types';
import { countriesTableTranslations } from './translations';
import { CountriesTableFiltersForm, CountriesTableTransFields } from './types';
import styles from './countriesTable.module.scss';

const CountriesTable: FC = () => {
  const {
    countriesStore: { countriesList, getCountriesList, changeCountriesStatus, setCountryData },
    mainStore: { user, getUser },
    usersStore: { setUsersCountryCodes },
  } = useStore();

  // Activating or deactivating a country changes the admins' country access on the backend:
  // refresh the navbar switcher (profile) and drop the cached list of the admin-user popup.
  const changeStatusAndRefreshAccess = useCallback(
    (req: IChangeCountryStatusReq) =>
      changeCountriesStatus(req).then((id) => {
        getUser();
        setUsersCountryCodes(null);
        return id;
      }),
    []
  );

  const [filtered, setFiltered] = useState<ICountryData[]>([]);

  const navigate = useNavigate();

  const { t } = useMyTranslations<CountriesTableTransFields>([countriesTableTranslations.name]);

  const { mutate, isLoading: isFetching } = useMutation(getCountriesList, {
    mutationKey: 'getCountriesList',
    retry: false,
  });

  useEffect(() => {
    mutate();
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<CountriesTableFiltersForm>();

  const { watch } = useFormMethods;
  const searchD = useDebounce(watch('search'));

  useEffect(() => {
    setFiltered(
      searchD
        ? countriesList?.filter(
            (item) =>
              item.defaultEngName.toLowerCase().includes(searchD.toLowerCase()) ||
              item.code.toLowerCase().includes(searchD.toLowerCase())
          ) || []
        : countriesList || []
    );
  }, [searchD, countriesList]);

  const onEditClick = useCallback((item: ICountryData) => {
    setCountryData(item);
    navigate(`${item.id}`);
  }, []);

  const columns: Column<ICountryData>[] = useMemo(
    () => [
      {
        Header: t('code'),
        id: 'code',
        Cell: ({
          row: {
            original,
            original: { code },
          },
        }: CellProps<ICountryData>) => (
          <button className={styles.avatar} onClick={() => onEditClick(original)}>
            <p className={styles.name}>{code}</p>
          </button>
        ),
        accessor: ({ code }) => code,
        width: 4,
      },
      {
        Header: t('defaultEngName'),
        id: 'defaultEngName',
        accessor: ({ defaultEngName }) => defaultEngName,
        width: 4,
      },
      {
        Header: t('domain'),
        id: 'domain',
        accessor: ({ domain }) => domain,
        width: 4,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status, code },
          },
        }: CellProps<ICountryData>) => (
          <StatusCell<IChangeCountryStatusReq>
            id={id}
            status={status}
            openPopUpData={{
              title: t('confirmationTitle'),
              description: t('approve-text'),
              buttonLabel: t('confirm'),
              formName: 'confirmWithCode',
              cbArgs: { code },
            }}
            changeStatus={changeStatusAndRefreshAccess}
          />
        ),
        accessor: ({ status }) => status,
        width: 3,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<ICountryData>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button className={`${styles.editBtn}`} onClick={() => onEditClick(original)}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message={t('edit')}
              place='top'
            />
          </div>
        ),
        width: 3,
        disableSortBy: true,
      },
    ],
    [filtered]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <FormProvider {...useFormMethods}>
        <div className={styles.search}>
          <ControlledInput
            name='search'
            classNameProp={styles.searchInput}
            placeholder={t('search')}
            isRemoveMargin
            disabled={!countriesList}
          />
          <Button title={t('addBtn')} onClick={() => navigate('add')} stylesProp={{ padding: '8px 36px' }} />
        </div>
      </FormProvider>
      {isFetching ? (
        <AppLoader />
      ) : countriesList && countriesList.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...filtered]}
          simpleList
          isPaginated={countriesList.length > PAGE_SIZE}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(CountriesTable);
